/**
 * Contact channels for the order-detail «Позвонить» / «Написать» menus
 * (TASK-4836).
 *
 * Two things live here, both deliberately outside the component so they can be
 * tested without a DOM:
 *
 * 1. **Link building.** Every channel takes the number digits-only — `wa.me`,
 *    `t.me` and `viber://` all reject `+`, spaces and dashes — while `tel:` and
 *    `sms:` keep the raw string, which those handlers parse themselves.
 * 2. **The remembered channel.** The dispatcher picks the same messenger dozens
 *    of times a day; the last one used is hoisted to the top of the menu so the
 *    common case is one click. Stored per browser, never per order.
 *
 * **Why `needsAppConfirm` matters.** A `viber://` link makes Chrome raise its own
 * "Open Viber?" dialog — a browser-owned prompt that a page cannot restyle,
 * suppress or detect. `https://` channels (WhatsApp, Telegram) open a tab with no
 * prompt at all. The flag lets the UI warn BEFORE handing off, so the system
 * dialog is expected rather than surprising, and the dispatcher can back out —
 * or copy the number — without seeing it.
 */

export type ContactChannel =
  | "whatsapp"
  | "telegram"
  | "viber"
  | "sms"
  | "system";

export interface ContactLink {
  /** The URL to open. */
  url: string;
  /**
   * True when the URL uses a custom app scheme, so the browser will raise its
   * own "Open <app>?" confirmation that we can neither style nor suppress.
   */
  needsAppConfirm: boolean;
}

/** Digits only — `wa.me` / `t.me` / `viber://` reject `+`, spaces and dashes. */
export function phoneDigits(phone: string): string {
  return phone.replace(/\D/g, "");
}

/**
 * Build the deep link for one channel.
 *
 * `viber` has no call-specific link: `chat?number=` opens the contact, where the
 * call button is one tap away. That is better than omitting Viber from the call
 * menu when a dispatcher uses it daily.
 */
export function contactLink(
  channel: ContactChannel,
  phone: string,
): ContactLink {
  const digits = phoneDigits(phone);
  switch (channel) {
    case "whatsapp":
      return { url: `https://wa.me/${digits}`, needsAppConfirm: false };
    case "telegram":
      return { url: `https://t.me/+${digits}`, needsAppConfirm: false };
    case "viber":
      return {
        url: `viber://chat?number=%2B${digits}`,
        needsAppConfirm: true,
      };
    case "sms":
      return { url: `sms:${phone}`, needsAppConfirm: true };
    case "system":
      return { url: `tel:${phone}`, needsAppConfirm: true };
  }
}

const STORAGE_KEY = "orders_contact_channel";

const VALID: readonly ContactChannel[] = [
  "whatsapp",
  "telegram",
  "viber",
  "sms",
  "system",
];

/**
 * The channel used last, or null.
 *
 * Storage can throw (private mode, disabled cookies) and can hold anything a
 * previous version wrote, so the value is both guarded and validated — an
 * unrecognised string is treated as "nothing remembered" rather than being
 * passed on to build a nonsense link.
 */
export function readPreferredChannel(): ContactChannel | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return VALID.includes(raw as ContactChannel)
      ? (raw as ContactChannel)
      : null;
  } catch {
    return null;
  }
}

export function writePreferredChannel(channel: ContactChannel): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, channel);
  } catch {
    // Storage full or disabled — remembering is a convenience, never a
    // requirement. The menu still works, it just does not reorder.
  }
}

/**
 * Order `channels` so the remembered one comes first, preserving the relative
 * order of the rest.
 *
 * Returns a NEW array; the caller's list is a module constant and must not be
 * mutated (that would make the order depend on how many times this ran).
 */
export function orderByPreference<T extends { channel: ContactChannel }>(
  items: readonly T[],
  preferred: ContactChannel | null,
): T[] {
  if (preferred === null) return [...items];
  const first = items.filter((i) => i.channel === preferred);
  const rest = items.filter((i) => i.channel !== preferred);
  return [...first, ...rest];
}
